import h from 'hyperscript';

import { assert } from '@disy/lib/custom-error';

import { getLabelShortNamesWithSelected } from 'cadenza/admin/data-protections-contexts/data-protection-contexts-utils';
import { readDataProtectionConfiguration } from 'cadenza/api-client/data-protection-api';
import {
  type DataProtectionConfigurationDto,
  type DataProtectionDescriptionDto,
} from 'cadenza/api-client/dto/data-protection-dto';
import type { DataProtectionMode } from 'cadenza/api-client/dto/data-protection-mode';
import type { RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import { DataProtectionContextPicker } from 'cadenza/data-protection-context/data-protection-context-picker';
import type { DataProtectionContextId } from 'cadenza/repository/model/data-protection-context-id.gen';

import './data-protection-assignment-panel.css';
import i18n from './data-protection-assignment-panel.properties';

const COMPONENT_NAME = 'd-data-protection-assignment-panel';

interface DataProtectionAssignmentPanelOptions {
  presetDataProtectionContextId?: DataProtectionContextId;
  presetDataProtectionDescription?: DataProtectionDescriptionDto;
  pickerOnly?: boolean;
  addSeparator?: boolean /* low-risk adjustment for 9.2 */;
}

export class DataProtectionAssignmentPanel extends HTMLElement {
  #repository;
  #presetDataProtectionContextId;
  #presetDataProtectionDescription;
  #dataProtectionMode: DataProtectionMode;

  #dpcPicker?: DataProtectionContextPicker;

  #dataProtectionConfiguration!: DataProtectionConfigurationDto;

  #pickerOnly: boolean;
  #addSeparator: boolean;

  constructor(repository: RepositoryDto, options: DataProtectionAssignmentPanelOptions = {}) {
    super();
    this.#repository = repository;
    this.#presetDataProtectionContextId = options.presetDataProtectionContextId;
    this.#presetDataProtectionDescription = options.presetDataProtectionDescription;
    this.#pickerOnly = options.pickerOnly ?? false;
    this.#addSeparator = options.addSeparator ?? true;
    this.#dataProtectionMode = repository.dataProtectionMode!;
    assert(
      this.#dataProtectionMode && this.#dataProtectionMode !== 'none',
      'Data protection mode cannot be undefined or none',
    );
    this.classList.add(COMPONENT_NAME);

    this.#loadData();
  }

  async #loadData() {
    this.#dataProtectionConfiguration = await readDataProtectionConfiguration()!;
    this.append(this.#createContent());
  }

  #createContent() {
    const mainPanel = h(`.${COMPONENT_NAME}--main-panel.d-stack-v.space-4`);
    if (this.#addSeparator) {
      mainPanel.append(h('hr'));
    }
    mainPanel.append(h('h2', i18n('dataProtection.title')));

    if (this.#dataProtectionMode === 'contexts') {
      this.#dpcPicker = new DataProtectionContextPicker(this.#repository, this.#presetDataProtectionContextId);
      mainPanel.append(this.#dpcPicker);
    }

    if (!this.#pickerOnly) {
      mainPanel.append(
        h(`.${COMPONENT_NAME}--labels-panel`, [
          h('form-group.form-group-xs', [
            h('label', i18n('dataConfidentialityLevel.label')),
            this.#createDataConfidentialityLevelRadioGroup(),
          ]),
          h('form-group.form-group-xs', [
            h('label', i18n('dataProtectionLabel.label')),
            this.#createDataProtectionLabelsCheckboxes(),
          ]),
        ]),
      );
    }
    return mainPanel;
  }

  #createDataConfidentialityLevelRadioGroup() {
    const defaultConfidentialityLevel =
      this.#presetDataProtectionDescription?.dataConfidentialityLevel ||
      this.#dataProtectionConfiguration.defaultConfidentialityLevel;
    const levels = this.#dataProtectionConfiguration.confidentialityLevels;
    const radioButtons = levels?.map(({ id, shortName }) => {
      return h('label.d-form-check', {}, [
        h('input', {
          type: 'radio',
          name: 'data-confidentiality-level',
          value: id,
          checked: id === defaultConfidentialityLevel,
        }),
        shortName,
      ]);
    });
    return h<HTMLElement>('div', radioButtons);
  }

  #createDataProtectionLabelsCheckboxes() {
    const defaultLabels = this.#presetDataProtectionDescription?.dataProtectionLabels || [];
    const radioButtons = getLabelShortNamesWithSelected(this.#dataProtectionConfiguration, defaultLabels).map(
      ({ id, shortName, selected }) =>
        h('label.d-form-check', {}, [
          h('input', {
            type: 'checkbox',
            name: 'data-protection-label',
            value: id,
            checked: selected,
          }),
          shortName,
        ]),
    );
    return h<HTMLElement>('div', radioButtons);
  }

  get selectedDataProtectionContextId() {
    return this.#dpcPicker?.selectedDataProtectionContextId;
  }

  get selectedDataConfidentialityLevelId() {
    return this.querySelector<HTMLInputElement>('[name = "data-confidentiality-level"]:checked')?.value;
  }

  get dataProtectionContextId() {
    return this.#dataProtectionMode === 'contexts' ? this.selectedDataProtectionContextId : undefined;
  }

  get dataProtectionDescription() {
    return {
      dataConfidentialityLevel: this.selectedDataConfidentialityLevelId,
      dataProtectionLabels: this.selectedDataProtectionLabelIds,
    } as unknown as DataProtectionDescriptionDto;
  }

  get selectedDataProtectionLabelIds() {
    return [...this.querySelectorAll<HTMLInputElement>('[name = "data-protection-label"]:checked')].map(
      (input) => input.value,
    );
  }
}

customElements.define(COMPONENT_NAME, DataProtectionAssignmentPanel);
